import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { forceEmailSend } from 'api/email';
import { ForceEmailSendRequest } from 'types';
import { useCampaignId } from 'hooks';
import { EMAIL_TYPES } from '@alloy-ch/rcplus-alloy-selfservice-email-service';

interface EmailTriggerSidebarProps {
  open: boolean;
  onClose: () => void;
}

// Map EMAIL_TYPES constants to display-friendly options
const EMAIL_TYPE_OPTIONS = [
  {
    value: EMAIL_TYPES.CAMPAIGN_CREATION_CONFIRMATION,
    label: 'Campaign Creation Confirmation',
    requiresCampaign: true,
  },
  { value: EMAIL_TYPES.CAMPAIGN_GOES_LIVE, label: 'Campaign Goes Live', requiresCampaign: true },
  { value: EMAIL_TYPES.CAMPAIGN_ENDS, label: 'Campaign Ends', requiresCampaign: true },
  { value: EMAIL_TYPES.CAMPAIGN_STOPPED, label: 'Campaign Stopped', requiresCampaign: true },
  { value: EMAIL_TYPES.CAMPAIGN_FIRST_CLICKS, label: 'Campaign First Clicks', requiresCampaign: true },
  { value: EMAIL_TYPES.CAMPAIGN_WEEKLY_SUMMARY_GOOD, label: 'Campaign Weekly Summary (Good)', requiresCampaign: true },
  { value: EMAIL_TYPES.CAMPAIGN_WEEKLY_SUMMARY_BAD, label: 'Campaign Weekly Summary (Bad)', requiresCampaign: true },
  { value: EMAIL_TYPES.GA4_SETUP_SUCCESS, label: 'GA4 Setup Success', requiresCampaign: true },
  { value: EMAIL_TYPES.BAD_PERFORMANCE, label: 'Bad Performance', requiresCampaign: true },
  { value: EMAIL_TYPES.NO_ENGAGEMENTS, label: 'No Engagements', requiresCampaign: true },
  { value: EMAIL_TYPES.DISCOUNT_50, label: 'Discount 50%', requiresCampaign: false },
  { value: EMAIL_TYPES.CAMPAIGN_STOPPED_MANUALLY, label: 'Campaign Stopped Manually', requiresCampaign: true },
];

export const EmailTriggerSidebar: React.FC<EmailTriggerSidebarProps> = ({ open, onClose }) => {
  const [selectedEmailType, setSelectedEmailType] = useState('');
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const location = useLocation();
  const campaignId = useCampaignId();

  // Check if we're on a campaign page
  const isCampaignPage = location.pathname.includes('/campaigns/') && campaignId;

  // Filter email types based on context
  const availableEmailTypes = EMAIL_TYPE_OPTIONS.filter((emailType) => {
    if (emailType.requiresCampaign && !isCampaignPage) {
      return false;
    }
    return true;
  });

  const sendEmailMutation = useMutation({
    mutationFn: (request: ForceEmailSendRequest) => forceEmailSend(request),
    onSuccess: (data) => {
      setResult({ success: data.success, message: data.message });
    },
    onError: (error: any) => {
      setResult({
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to send email',
      });
    },
  });

  const handleSendEmail = () => {
    if (!selectedEmailType) return;

    const selectedEmail = EMAIL_TYPE_OPTIONS.find((email) => email.value === selectedEmailType);
    const request: ForceEmailSendRequest = {
      emailType: selectedEmailType,
    };

    // Add campaignId if required and available
    if (selectedEmail?.requiresCampaign && campaignId) {
      request.campaignId = campaignId;
    }

    sendEmailMutation.mutate(request);
  };

  const handleClose = () => {
    setSelectedEmailType('');
    setResult(null);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          padding: 3,
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Email Trigger (Test)</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="body2" color="text.secondary" mb={2}>
        Manually trigger emails for testing purposes.
        {isCampaignPage && <> Campaign-specific emails will use Campaign ID: {campaignId}</>}
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Email Type</InputLabel>
        <Select
          value={selectedEmailType}
          onChange={(e) => setSelectedEmailType(e.target.value)}
          label="Select Email Type"
        >
          {availableEmailTypes.map((emailType) => (
            <MenuItem key={emailType.value} value={emailType.value}>
              {emailType.label}
              {emailType.requiresCampaign && ' (Campaign)'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSendEmail}
        disabled={!selectedEmailType || sendEmailMutation.isPending}
        sx={{ mb: 2 }}
      >
        {sendEmailMutation.isPending ? 'Sending...' : 'Send Email'}
      </Button>

      {result && (
        <Alert
          severity={result.success ? 'success' : 'error'}
          sx={{ mt: 2 }}
          icon={result.success ? <CheckCircleIcon /> : <ErrorIcon />}
        >
          {result.message}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="caption" color="text.secondary">
        This feature is only available in DEV/STG environments for administrators.
      </Typography>
    </Drawer>
  );
};
